/**
 * @file        src/pages/account/ActionCenter.jsx
 * @description Account Management → Action Centre. Every open item across the
 *              twelve queues this console keeps, oldest first, with the next
 *              real step on each row.
 *
 *              WHOLE-BUSINESS ON PURPOSE, not a B2C/Corporate pair — the same
 *              §9d exception Transaction Log, Abandoned Payments and Payment
 *              Reconciliation already live under. Four of the queues have no
 *              customer type at all (most abandoned checkouts are guests), so an
 *              audience split would file a large part of the work under neither
 *              screen. Instead the audience is a read-only COLUMN that can be
 *              filtered — exactly the shape §16 settled on for Abandoned
 *              Payments. The two dashboards keep their own audience-scoped
 *              panels; this is the place you work from.
 *
 *              THE CHIPS AND THE TABLE COUNT ONE SET. Both come from the same
 *              request (`queues` rides along with the rows), so a chip can never
 *              advertise a number the table cannot list. The chips deliberately
 *              ignore only the `kind` filter — selecting one queue must not zero
 *              the others' counts — while every other filter applies to both,
 *              so a chip still counts what its own click would show.
 *
 *              "DONE" IS THE RECORD'S OWN RESOLUTION. There is no action table
 *              behind this screen (see actionCenter.repo): an item leaves
 *              because the thing it names actually changed state. Two queues
 *              have one unambiguous terminal step and close inline; the rest
 *              carry you to the screen that owns the decision, because approving
 *              a corporate application creates a login, settling a due needs the
 *              amount and the reference, and a policy request needs the
 *              insurer's PDF. Supplier-owned rows offer no action at all.
 * @author      Kinetika Labs
 * @created     2026-09-24
 * @changelog
 *   - 2026-09-24 Claude — Initial.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import { listActionItems, resolveActionItem } from '../../api/actionCenter';
import { targetFor, hrefFor, stepLabel } from '../../lib/actionTargets';
import { inr, formatDateTime } from '../../lib/format';
import { apiErrorMessage } from '../../api/client';
import { toast } from '../../lib/toast';

const PAGE_SIZE = 25;

const OWNER_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'us', label: 'Waiting on us' },
  { value: 'supplier', label: 'Waiting on the supplier' },
];
const CUSTOMER_OPTIONS = [
  { value: '', label: 'All customers' },
  { value: 'guest', label: 'Guest' },
  { value: 'retail', label: 'Retail' },
  { value: 'corporate', label: 'Corporate' },
];
const CUSTOMER_LABEL = { guest: 'Guest', retail: 'Retail', corporate: 'Corporate' };
// Filters that live in the URL, so a view can be shared and Back works. NOT
// `audience` — this screen is whole-business, and a URL-settable audience is the
// thing §9d forbids. The customer TYPE below is a column filter, not an
// audience: it cannot cross a screen boundary because there is no other screen.
const URL_KEYS = ['kind', 'owner', 'customerType', 'brand', 'search'];

/** How long it has waited, loud once that stops being reasonable. */
function Age({ days }) {
  const stale = days >= 7;
  return (
    <span className={`ac-age${stale ? ' stale' : ''}`}>
      {days <= 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`}
    </span>
  );
}

export default function ActionCenter() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState(params.get('search') || '');
  const { options: brandOptions } = useBrands();

  const filters = {};
  for (const k of URL_KEYS) {
    const v = params.get(k) || '';
    if (v) filters[k] = v;
  }

  function setFilters(patch, { resetPage = true } = {}) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
    if (resetPage) setPage(1);
  }

  const queryParams = { ...filters, page, pageSize: PAGE_SIZE };
  const { data, isLoading } = useQuery({
    queryKey: qk.actionCenter.items(queryParams),
    queryFn: () => listActionItems(queryParams).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });

  const rows = data?.rows || [];
  const queues = data?.queues || [];
  const total = data?.total || 0;
  const openTotal = queues.reduce((n, q) => n + q.count, 0);

  const done = useMutation({
    mutationFn: ({ kind, id }) => resolveActionItem(kind, id),
    onSuccess: (res) => {
      // A stale tab closing something already closed is a SUCCESS, not an
      // error — the work is done either way, and an error toast would send
      // somebody looking for a problem that does not exist.
      toast.ok(res?.data?.alreadyDone ? 'Already done' : 'Marked done');
      qc.invalidateQueries({ queryKey: qk.actionCenter.root });
    },
    onError: (e) => toast.err('Could not close it', apiErrorMessage(e, 'Please try again.')),
  });

  const anyFilter = URL_KEYS.some((k) => params.get(k));

  return (
    <>
      <PageHead
        title="Action Centre"
        subtitle={`Everything open across the console · ${openTotal} item${openTotal === 1 ? '' : 's'}`}
        actions={(
          <Link className="btn ghost sm" to="/account/activity-log">
            <Icon name="clock" /> Activity Log
          </Link>
        )}
      />

      {/* Whole business, stated rather than assumed — every other screen in
          Operations is one audience or the other, so a reader is entitled to
          ask which this one is. */}
      <p className="muted" style={{ marginTop: -6, marginBottom: 12, fontSize: '.78rem' }}>
        Whole business — retail and corporate together, plus the queues that
        belong to neither. Filter by customer to narrow it; the two dashboards
        each carry their own audience's panel.
      </p>

      <div className="ac-chips">
        <button
          type="button"
          className={`ac-chip${filters.kind ? '' : ' active'}`}
          onClick={() => setFilters({ kind: '' })}
        >
          All queues <span className="n">{openTotal}</span>
        </button>
        {queues.filter((q) => q.count > 0).map((q) => (
          <button
            key={q.key}
            type="button"
            className={`ac-chip${filters.kind === q.key ? ' active' : ''}`}
            onClick={() => setFilters({ kind: filters.kind === q.key ? '' : q.key })}
            title={q.owner === 'supplier' ? 'Waiting on the supplier' : 'Waiting on us'}
          >
            <Icon name={targetFor(q.key).icon} size={13} />
            {q.label} <span className="n">{q.count}</span>
          </button>
        ))}
      </div>

      <article className="panel glass">
        <form
          className="search-row"
          onSubmit={(e) => { e.preventDefault(); setFilters({ search: searchDraft }); }}
        >
          <div className="field" style={{ flex: "2 1 240px" }}>
            <label>Reference or name</label>
            <input
              placeholder="Booking ref, transaction id, company…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Waiting on</label>
            <Select value={filters.owner || ''} onChange={(v) => setFilters({ owner: v })} options={OWNER_OPTIONS} />
          </div>
          <div className="field">
            <label>Customer</label>
            <Select
              value={filters.customerType || ''}
              onChange={(v) => setFilters({ customerType: v })}
              options={CUSTOMER_OPTIONS}
            />
          </div>
          <div className="field">
            <label>Brand</label>
            {/* 'Unknown' is a real option, not a courtesy: credit dues and
                payment discrepancies carry no brand column at all, so narrowing
                to a storefront excludes them — and a tenant running two brands
                would otherwise filter to one and quietly stop seeing every
                outstanding due. Same token as Abandoned Payments. */}
            <Select
              value={filters.brand || ''}
              onChange={(v) => setFilters({ brand: v })}
              options={[
                { value: '', label: 'All brands' },
                ...brandOptions,
                { value: 'unknown', label: 'No brand (dues, discrepancies)' },
              ]}
            />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
          <button
            type="button"
            className="btn ghost"
            disabled={!anyFilter}
            onClick={() => { setSearchDraft(''); setParams(new URLSearchParams(), { replace: true }); setPage(1); }}
          >
            Clear
          </button>
        </form>

        {/* Say what the filter just did. Two queues have no brand to match, so a
            brand filter hides them — visibly, or not at all. */}
        {filters.brand && filters.brand !== 'unknown' ? (
          <p className="muted" style={{ fontSize: '.72rem', margin: '0 0 10px' }}>
            Showing one storefront — credit dues and payment discrepancies carry
            no brand and are not listed here. Pick “No brand” to see them.
          </p>
        ) : null}

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Queue</th>
                  <th>Reference</th>
                  <th>Who</th>
                  <th>Customer</th>
                  <th>State</th>
                  <th>Amount</th>
                  <th>Waiting</th>
                  <th>Since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      {anyFilter ? 'Nothing open matches these filters.' : 'Nothing is open. The console is clear.'}
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const t = targetFor(r.kind);
                  const busy = done.isPending
                    && done.variables?.kind === r.kind && done.variables?.id === r.id;
                  return (
                    <tr key={`${r.kind}:${r.id}`}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span className={`s-ic ${t.tone}`} style={{ width: 26, height: 26, borderRadius: 8 }}>
                            <Icon name={t.icon} size={13} />
                          </span>
                          {t.what}
                        </span>
                      </td>
                      <td><b>{r.reference || '—'}</b></td>
                      <td>{r.party || <span className="muted">—</span>}</td>
                      <td>{CUSTOMER_LABEL[r.customerType] || r.customerType}</td>
                      <td><Chip tone={r.owner === 'supplier' ? 'pend' : 'warn'}>{r.state}</Chip></td>
                      <td>{r.amount === null ? <span className="muted">—</span> : inr(r.amount)}</td>
                      <td>
                        {/* The distinction the whole screen turns on: work we can
                            finish, and work somebody else owes us an answer on. */}
                        {r.owner === 'supplier' ? 'Supplier' : 'Us'}
                      </td>
                      <td>
                        <Age days={r.ageDays} />
                        <div className="muted" style={{ fontSize: '.68rem' }}>{formatDateTime(r.occurredAt)}</div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {r.resolvable ? (
                          <button
                            type="button"
                            className="btn primary sm"
                            disabled={busy}
                            onClick={() => done.mutate({ kind: r.kind, id: r.id })}
                          >
                            {busy ? 'Saving…' : 'Mark done'}
                          </button>
                        ) : (
                          <Link className="btn ghost sm" to={hrefFor(r)}>
                            {stepLabel(r)} <Icon name="chev" size={13} />
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
      </article>
    </>
  );
}
